/** XeAgents.ai **/
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface Provider {
  id: string;
  name: string;
  specialty: string;
  location: string | null;
  calcomEventTypeId: number | null;
  active: boolean;
}

const EMPTY = { name: "", specialty: "General Practice", location: "", calcomEventTypeId: "", active: true };

export default function ProvidersPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Provider | "new" | null>(null);
  const [form, setForm] = useState(EMPTY);

  const { data: providers = [], isLoading } = useQuery<Provider[]>({ queryKey: ["/api/healthcare/providers"] });

  function open(target: Provider | "new") {
    setEditing(target);
    setForm(
      target === "new"
        ? EMPTY
        : { name: target.name, specialty: target.specialty, location: target.location ?? "", calcomEventTypeId: target.calcomEventTypeId ? String(target.calcomEventTypeId) : "", active: target.active },
    );
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, calcomEventTypeId: form.calcomEventTypeId.trim() === "" ? null : Number(form.calcomEventTypeId) };
      const res = editing === "new" ? await apiRequest("POST", "/api/healthcare/providers", payload) : await apiRequest("PUT", `/api/healthcare/providers/${(editing as Provider).id}`, payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/providers"] });
      setEditing(null);
    },
    onError: (e: any) => toast({ title: "Could not save the doctor", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/healthcare/providers/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/healthcare/providers"] }),
    onError: (e: any) => toast({ title: "Could not remove the doctor", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Doctors</h3>
          <p className="text-sm text-muted-foreground">
            Each doctor books against their own Cal.com event type, so callers can ask for a named doctor or any doctor in a specialty. With no doctors added, the receptionist books against your default Cal.com event type.
          </p>
        </div>
        <Button onClick={() => open("new")} data-testid="button-add-provider">
          <Plus className="mr-2 h-4 w-4" /> Add doctor
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Specialty</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Cal.com event type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {providers.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                {isLoading ? "Loading…" : "No doctors yet."}
              </TableCell>
            </TableRow>
          )}
          {providers.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell>{p.specialty}</TableCell>
              <TableCell>{p.location || "—"}</TableCell>
              <TableCell>{p.calcomEventTypeId ?? <span className="text-muted-foreground">Account default</span>}</TableCell>
              <TableCell>{p.active ? <Badge variant="secondary">Bookable</Badge> : <Badge variant="outline">Paused</Badge>}</TableCell>
              <TableCell className="text-right">
                <Button size="icon" variant="ghost" aria-label={`Edit ${p.name}`} onClick={() => open(p)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" aria-label={`Remove ${p.name}`} onClick={() => window.confirm(`Remove ${p.name}? Past appointments are kept.`) && remove.mutate(p.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add doctor" : "Edit doctor"}</DialogTitle>
            <DialogDescription>Create the doctor's event type in Cal.com first, then paste its id here (it is the number in the event type's URL).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prov-name">Name</Label>
              <Input id="prov-name" value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr Priya Nair" data-testid="input-provider-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-spec">Specialty</Label>
              <Input id="prov-spec" value={form.specialty} maxLength={80} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="General Practice, Paediatrics, Dental…" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-loc">Location (optional)</Label>
              <Input id="prov-loc" value={form.location} maxLength={160} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Level 2, Main Street" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-cal">Cal.com event type id</Label>
              <Input id="prov-cal" inputMode="numeric" value={form.calcomEventTypeId} onChange={(e) => setForm({ ...form, calcomEventTypeId: e.target.value.replace(/[^0-9]/g, "") })} placeholder="Leave empty to use the account default" />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm">Available for booking</span>
              <Switch checked={form.active} onCheckedChange={(active) => setForm({ ...form, active })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name.trim() || !form.specialty.trim()} data-testid="button-save-provider">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
